import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';
import { Button, Checkbox, Field, Select } from '@bb/ui';
import type { AdminApi, ReportStart, RetentionReport, RetentionRequest, RetentionRow } from '../../../api/client.ts';
import type { PropertyCondition } from '../../campaigns/trigger/actionBased.ts';
import { CustomEventFields } from '../../campaigns/trigger/TriggerEditor.tsx';
import { PeriodPicker } from '../PeriodPicker.tsx';
import { periodProblem } from '../period.ts';
import type { ReportPanelProps } from './FunnelReportPanel.tsx';
import { groupLabel, ReportError } from './ReportError.tsx';
import { eventIssues, pct, reportPeriodProblem, RETENTION_DAY_OPTIONS, RETENTION_DEFAULT_DAYS, RETENTION_MAX_DAYS, START_LABELS, startsFor } from './reportBuilder.ts';

/** A cell of the grid: the rate with how many returned, «…» while day N is not over for anyone; shaded by the rate. */
function Cell({ cell }: { cell: RetentionRow['cells'][number] }) {
  if (cell.returned === null) {
    return (
      <td className="retention-grid__pending" title={`Day ${cell.day} is not over yet`}>
        <span aria-label={`Day ${cell.day} not over yet`}>…</span>
      </td>
    );
  }
  const style = { '--retention': String(cell.rate ?? 0) } as CSSProperties;
  return (
    <td className="retention-grid__cell" style={style} title={`${cell.returned.toLocaleString('en-US')} of ${cell.eligible.toLocaleString('en-US')} returned on day ${cell.day}`}>
      <span className="push-table__count">{pct(cell.rate)}</span>
      <span className="push-table__rate">{cell.returned.toLocaleString('en-US')}</span>
    </td>
  );
}

/** Cohorts by start day, then all of them together: each day N over the users whose day N is over. */
function RetentionGrid({ report }: { report: RetentionReport }) {
  return (
    <div className="push-table-scroll">
      <table className="table push-table retention-grid" aria-label="Retention by start day">
        <thead>
          <tr>
            <th scope="col">Start day</th>
            <th scope="col">Users</th>
            {report.days.map((d) => (
              <th key={d} scope="col">
                Day {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.cohorts.map((c) => (
            <tr key={c.cohortDay}>
              <th scope="row">{c.cohortDay}</th>
              <td>{c.size.toLocaleString('en-US')}</td>
              {c.cells.map((cell) => (
                <Cell key={cell.day} cell={cell} />
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">All cohorts</th>
            <td>{report.total.size.toLocaleString('en-US')}</td>
            {report.total.cells.map((cell) => (
              <Cell key={cell.day} cell={cell} />
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function GroupsGrid({ report }: { report: RetentionReport }) {
  const groups = report.groups ?? [];
  if (groups.length === 0) return <p className="hint">No variant or control users started in this period.</p>;
  return (
    <div className="push-table-scroll">
      <table className="table push-table retention-grid" aria-label="Retention by variant">
        <thead>
          <tr>
            <th scope="col">Group</th>
            <th scope="col">Users</th>
            {report.days.map((d) => (
              <th key={d} scope="col">
                Day {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.variantApiId}>
              <th scope="row">{groupLabel(g)}</th>
              <td>{g.total.size.toLocaleString('en-US')}</td>
              {g.total.cells.map((cell) => (
                <Cell key={cell.day} cell={cell} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Retention report of a campaign or Canvas (BB-135, SEG-010): users by the environment day of their first start in the
 * period, and the share who came back — a session or a chosen event — on calendar day start + N. A day counts only
 * once it is over, so young cohorts do not drag the total down; optionally per variant with control.
 */
export function RetentionReportPanel({ environmentId, source, channels, period, onPeriodChange }: ReportPanelProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const starts = startsFor(source, channels);
  const [start, setStart] = useState<ReportStart>(starts[0]!);
  const [returnKind, setReturnKind] = useState<'session' | 'event'>('session');
  const [event, setEvent] = useState<{ name: string; propertyFilters: PropertyCondition[] }>({ name: '', propertyFilters: [] });
  const [days, setDays] = useState<number[]>(RETENTION_DEFAULT_DAYS);
  const [byVariant, setByVariant] = useState(false);
  const [tried, setTried] = useState(false);
  const [submitted, setSubmitted] = useState<RetentionRequest | null>(null);
  const report = useQuery({ queryKey: ['reports', environmentId, 'retention', submitted], queryFn: () => api.retentionReport(environmentId, submitted!), enabled: submitted !== null, retry: false });

  const canvasVariants = 'canvasId' in source && start !== 'canvas_entered';
  const issues = returnKind === 'event' ? eventIssues(['returnEvent'], event.name, event.propertyFilters) : [];
  const periodIssue = reportPeriodProblem(period);
  const daysIssue = days.length === 0 ? 'Choose at least one day.' : null;
  const toggleDay = (d: number, on: boolean) => setDays(on ? [...days, d].sort((a, b) => a - b) : days.filter((x) => x !== d));
  const run = () => {
    setTried(true);
    if (issues.length || periodIssue || daysIssue) return;
    const next: RetentionRequest = {
      source,
      start,
      returnEvent: returnKind === 'session' ? { kind: 'session' } : { kind: 'event', name: event.name, propertyFilters: event.propertyFilters },
      days,
      from: period.from,
      to: period.to,
      ...(byVariant && !canvasVariants ? { groupBy: 'variant' as const } : {}),
    };
    if (JSON.stringify(next) === JSON.stringify(submitted)) void report.refetch();
    else setSubmitted(next);
  };
  const data = report.data;
  return (
    <section className="panel report-builder" aria-label="Retention report">
      <h2>Retention</h2>
      <PeriodPicker value={period} onChange={onPeriodChange} />
      {periodIssue && periodProblem(period) === null && (
        <p role="alert" className="field-error">
          {periodIssue}
        </p>
      )}
      <div className="stack-form">
        <Field label="Start">
          <Select options={starts.map((s) => ({ value: s, label: START_LABELS[s] }))} value={start} onChange={(e) => setStart(e.target.value as ReportStart)} />
        </Field>
        <Field label="Returned means">
          <Select
            options={[
              { value: 'session', label: 'Starts a session' },
              { value: 'event', label: 'Performs a custom event' },
            ]}
            value={returnKind}
            onChange={(e) => setReturnKind(e.target.value === 'event' ? 'event' : 'session')}
          />
        </Field>
        {returnKind === 'event' && (
          <div role="group" aria-label="Return event">
            <CustomEventFields
              environmentId={environmentId}
              readOnly={false}
              issues={tried ? issues : []}
              path={['returnEvent']}
              spec={{ kind: 'custom_event', event: event.name, propertyFilters: event.propertyFilters }}
              onChange={(next) => {
                if (next.kind === 'custom_event') setEvent({ name: next.event, propertyFilters: next.propertyFilters });
              }}
            />
          </div>
        )}
        <fieldset className="retention-days">
          <legend>Days after the start</legend>
          {RETENTION_DAY_OPTIONS.map((d) => (
            <Checkbox key={d} label={`Day ${d}`} checked={days.includes(d)} disabled={!days.includes(d) && days.length >= RETENTION_MAX_DAYS} onCheckedChange={(on) => toggleDay(d, on)} />
          ))}
        </fieldset>
        {tried && daysIssue && (
          <p role="alert" className="field-error">
            {daysIssue}
          </p>
        )}
        <Checkbox label="Compare variants and control" checked={byVariant && !canvasVariants} disabled={canvasVariants} onCheckedChange={setByVariant} />
        {canvasVariants && <p className="hint">Canvas variants are known from the entry: choose the start «Entered the Canvas» to compare them.</p>}
        <div>
          <Button onClick={run} loading={report.isFetching}>
            Run report
          </Button>
        </div>
      </div>
      {submitted && (
        <div className="report-result" aria-live="polite">
          {report.isError ? (
            <ReportError error={report.error} onRetry={() => void report.refetch()} />
          ) : !data ? (
            <p aria-busy="true">Running the retention report…</p>
          ) : (
            <>
              <p className="hint">
                {data.from} – {data.to}, {data.timezone}. Today in the environment is {data.today}: day N of a cohort counts once it has ended. Computed {new Date(data.generatedAt).toLocaleString('en-GB')}; results are kept for a few minutes.
              </p>
              {data.cohorts.length === 0 ? <p className="hint">Nobody started in this period.</p> : <RetentionGrid report={data} />}
              {data.groups && <GroupsGrid report={data} />}
              <details>
                <summary>How retention is counted</summary>
                <p>{data.definition.en}</p>
              </details>
            </>
          )}
        </div>
      )}
    </section>
  );
}
